import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getSubscriptionPlanList(
    $filter: subscriptionPlanFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getSubscriptionPlanList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        id
        name
        description
        period
      }
      pagination {
        pageNumber
        pageLimit
        totalCount
      }
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.SubscriptionPlanListState = {
  result: null,
  error: null,
  loading: false
};

export const getSubscriptionPlanListRequest = createAsyncThunk<
  TYPES.SubscriptionPlanList,
  {
    filter?: TYPES.SubscriptionPlanFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'subscription_plan/get_subscription_plan_list',
  async (
  params: {
    filter?: TYPES.SubscriptionPlanFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getSubscriptionPlanList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getSubscriptionPlanListSlice = createSlice({
  name: 'get_subscription_plan_list',
  initialState: initialState,
  reducers: {
    getSubscriptionPlanListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getSubscriptionPlanListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      getSubscriptionPlanListRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(getSubscriptionPlanListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getSubscriptionPlanListClear } =
getSubscriptionPlanListSlice.actions;

export const getSubscriptionPlanList = createSelector(
  (state: RootState) =>
  state.subscriptionPlan.getSubscriptionPlanList.result,
  (state: RootState) =>
  state.subscriptionPlan.getSubscriptionPlanList.error,
  (state: RootState) =>
  state.subscriptionPlan.getSubscriptionPlanList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getSubscriptionPlanListSlice.reducer;